
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// module components
import ContractForm from '@/modules/ContractsModule/components/ContractForm/index.vue'
import D24Form from '@/modules/ContractsModule/components/D24Form/index.vue'
import AttachDocumentForm from '@/modules/ContractsModule/components/AttachDocumentForm/index.vue'

// types
import { ContractTypeT, ContractT, ContractService } from '@/core/types/contracts.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// constants
import {
    CONTRACT_STATUSES,
    DOCUMENT_OPTIONS,
    RENEWAL_OPTIONS,
} from '@/core/constants/contracts.constants'
import { ContractsColumns, ContractsStatusColors } from '@/core/constants/columns/Contracts.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

const MAX_NAME_LENGTH_IN_TABLE = 12

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        'failed-fetch': FailedFetch,
        'contract-form': ContractForm,
        'attach-document-form': AttachDocumentForm,
        cheap: Cheap,
        'd24-form': D24Form,
        TheTable,
        TableScrollWrapper,
        Checkbox,
    },
    setup() {
        const state = reactive({
            contracts: new DataState<ContractT>({}),
            is_attach: false,
            is_attach_loading: false,
            contract_id_for_attach: null as number | null,
            is_add_contract: false,
            is_d24: false,
        })

        const filters_state = reactive({
            limit: 50,
            //
            number: null as string | null,
            bin: null as string | null,
            original_name: null as string | null,
            company_id: null as number | null,
            status_ids: [] as number[],
            type_ids: [] as number[],
            additionalTypeIds: [] as number[],
            subdivision_id: null as number | null,
            serviceIds: [] as number[],
            hasDocument: null as number | null,
            isAutoRenewal: null as number | null,
            hasResignedContract: null as number | null,
        })

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            contract_types: new DataState<ContractTypeT>({}),
            contract_types_additional: new DataState<ContractTypeT>({}),
            services: new DataState<ContractService>({}),
        })

        const loadServices = async () => {
            try {
                data_state.services.is_loading = true
                const response = await contracts.getServices()
                data_state.services.list = response.data

                data_state.services.is_failed = false
                data_state.services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.services.failure.catchError(error)

                if (!data_state.services.failure.error_state.is_cancel) {
                    data_state.services.is_failed = true
                }
            } finally {
                if (!data_state.services.failure.error_state.is_cancel) {
                    data_state.services.is_loading = false
                }
            }
        }

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({
                    page: 1,
                })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = {
                    page,
                    search,
                    limit: 9999,
                    isExtended: 0,
                    getAll: 1,
                }
                const response = await companies.getList(params)
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // types

        const loadContractTypes = async (page: number) => {
            data_state.contract_types.pagination.current_page = page

            try {
                data_state.contract_types.is_loading = true

                const params = {
                    page,
                    isMain: 1,
                }

                const response = await contracts.getTypes(params)
                data_state.contract_types.list = response.data
                data_state.contract_types.pagination.total_pages = response.meta.last_page

                data_state.contract_types.is_failed = false
                data_state.contract_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.contract_types.failure.catchError(error)

                if (!data_state.contract_types.failure.error_state.is_cancel) {
                    data_state.contract_types.is_failed = true
                }
            } finally {
                if (!data_state.contract_types.failure.error_state.is_cancel) {
                    data_state.contract_types.is_loading = false
                }
            }
        }

        const loadMoreContractTypes = async (page: number) => {
            if (page > data_state.contract_types.pagination.total_pages) return
            data_state.contract_types.pagination.current_page = page

            try {
                data_state.contract_types.is_loading = true

                const params = {
                    page,
                    isMain: 1,
                }

                const response = await contracts.getTypes(params)
                data_state.contract_types.list = data_state.contract_types.list.concat(
                    response.data
                )

                data_state.contract_types.pagination.total_pages = response.meta.last_page

                data_state.contract_types.is_failed = false
                data_state.contract_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.contract_types.failure.catchError(error)

                if (!data_state.contract_types.failure.error_state.is_cancel) {
                    data_state.contract_types.is_failed = true
                }
            } finally {
                if (!data_state.contract_types.failure.error_state.is_cancel) {
                    data_state.contract_types.is_loading = false
                }
            }
        }

        // types additional

        const loadContractTypesAdditional = async (page: number) => {
            data_state.contract_types_additional.pagination.current_page = page

            try {
                data_state.contract_types_additional.is_loading = true

                const params = {
                    page,
                    isMain: 0,
                }

                const response = await contracts.getTypes(params)
                data_state.contract_types_additional.list = response.data
                data_state.contract_types_additional.pagination.total_pages =
                    response.meta.last_page

                data_state.contract_types_additional.is_failed = false
                data_state.contract_types_additional.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.contract_types_additional.failure.catchError(error)

                if (!data_state.contract_types_additional.failure.error_state.is_cancel) {
                    data_state.contract_types_additional.is_failed = true
                }
            } finally {
                if (!data_state.contract_types_additional.failure.error_state.is_cancel) {
                    data_state.contract_types_additional.is_loading = false
                }
            }
        }

        const loadMoreContractTypesAdditional = async (page: number) => {
            if (page > data_state.contract_types_additional.pagination.total_pages) return
            data_state.contract_types_additional.pagination.current_page = page

            try {
                data_state.contract_types_additional.is_loading = true

                const params = {
                    page,
                    isMain: 0,
                }

                const response = await contracts.getTypes(params)
                data_state.contract_types_additional.list =
                    data_state.contract_types_additional.list.concat(response.data)

                data_state.contract_types_additional.pagination.total_pages =
                    response.meta.last_page

                data_state.contract_types_additional.is_failed = false
                data_state.contract_types_additional.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.contract_types_additional.failure.catchError(error)

                if (!data_state.contract_types_additional.failure.error_state.is_cancel) {
                    data_state.contract_types_additional.is_failed = true
                }
            } finally {
                if (!data_state.contract_types_additional.failure.error_state.is_cancel) {
                    data_state.contract_types_additional.is_loading = false
                }
            }
        }

        //

        const loadContracts = async (page: number) => {
            state.contracts.pagination.current_page = page

            // filters
            const params = {
                page,
                number: filters_state.number as string,
                bin: filters_state.bin as string,
                original_name: filters_state.original_name as string,
                company_id: filters_state.company_id as number,
                status_ids: filters_state.status_ids as number[],
                type_ids: filters_state.type_ids as number[],
                additionalTypeIds: filters_state.additionalTypeIds as number[],
                subdivision_id: filters_state.subdivision_id as number,
                serviceIds: filters_state.serviceIds as number[],
                hasDocument: filters_state.hasDocument,
                isAutoRenewal: filters_state.isAutoRenewal,
                hasResignedContract: filters_state.hasResignedContract,
                limit: filters_state.limit,
            }

            try {
                state.contracts.is_loading = true

                const response = await contracts.getList(params)

                state.contracts.list = response.data
                state.contracts.pagination.total_pages = response.meta.last_page
                state.contracts.pagination.total_items = response.meta.total

                state.contracts.is_failed = false
                state.contracts.failure.error_state.is_cancel = false
            } catch (error) {
                state.contracts.failure.catchError(error)

                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_failed = true
                }
            } finally {
                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_loading = false
                }
            }
        }

        const resetFilters = () => {
            filters_state.number = null
            filters_state.bin = null
            filters_state.original_name = null
            filters_state.company_id = null
            filters_state.status_ids = []
            filters_state.type_ids = []
            filters_state.subdivision_id = null
            filters_state.serviceIds = []
            filters_state.hasDocument = null
            filters_state.hasResignedContract = null
            filters_state.additionalTypeIds = []
            filters_state.isAutoRenewal = null

            loadContracts(1)
        }

        const startAttach = (contract_id: number) => {
            state.contract_id_for_attach = contract_id
            state.is_attach = true
        }

        const cancelAttach = () => {
            state.contract_id_for_attach = null
            state.is_attach = false
        }

        const attachDocument = async (form_data: FormData) => {
            if (!state.contract_id_for_attach) return

            try {
                state.is_attach_loading = true
                await contracts.attachDocument(form_data)
                await loadContracts(state.contracts.pagination.current_page)
                cancelAttach()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_attach_loading = false
            }
        }

        const onContractAdded = () => {
            state.is_add_contract = false
            loadContracts(state.contracts.pagination.current_page)
        }

        const cutName = (name: string): string => {
            if (name && name.length > MAX_NAME_LENGTH_IN_TABLE) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, MAX_NAME_LENGTH_IN_TABLE - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const exportExcel = async () => {
            try {
                state.contracts.is_loading = true
                const response = await contracts.exportExcel(filters_state)
                const download_url = window.URL.createObjectURL(response)
                const link = document.createElement('a')
                link.href = download_url
                link.target = '_blank'
                link.setAttribute('download', `Договоры`)
                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (e) {
                console.log(e)
            } finally {
                state.contracts.is_loading = false
            }
        }

        const toggleD24 = () => {
            state.is_d24 = !state.is_d24
        }

        const getCheckOrTimesIcon = (is_file: boolean) => {
            return `fas ${is_file ? 'fa-check text-green-500 mr-4' : 'fa-times text-red-500 mr-4'}`
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadContracts(state.contracts.pagination.current_page)
        }

        const getServicesStr = (services: { name: string }[]) => {
            return services.map((item) => item.name).join(', ')
        }

        //

        onBeforeMount(() => {
            loadContractTypes(1)
            loadContractTypesAdditional(1)
            loadCompanies(1)
            loadSubdivisions()
            loadServices()
            loadContracts(1)
        })

        //

        return {
            ContractsColumns,
            ContractsStatusColors,
            CONTRACT_STATUSES,
            DOCUMENT_OPTIONS,
            RENEWAL_OPTIONS,
            //
            state,
            filters_state,
            //
            loadServices,
            loadSubdivisions,
            loadContracts,
            resetFilters,
            startAttach,
            cancelAttach,
            attachDocument,
            onContractAdded,
            data_state,
            loadCompanies,
            searchCompanies,
            unshiftFindedCompany,
            cutName,
            exportExcel,
            getCheckOrTimesIcon,
            onLimitChange,
            //
            loadContractTypes,
            loadMoreContractTypes,
            loadContractTypesAdditional,
            loadMoreContractTypesAdditional,
            toggleD24,
            getServicesStr,
            //
            formatDateWithTimeFromBackend,
            formatDateFromBackend
        }
    },
})
